'use client'

import { useCallback, useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { api, queryKeys } from '@/lib/api'
import type {
  Announcement,
  NewsItem,
  SearchParams,
  SearchResult,
  ApiResponse,
} from '@/types'

// Generic hook for API data fetching
export function useApiData<T>(
  queryKey: readonly unknown[],
  apiCall: () => Promise<ApiResponse<T>>
) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await apiCall()
      if (!response || !api.utils.isSuccess(response)) {
        const message =
          response?.error || response?.message || 'Failed to fetch data'
        throw new Error(api.utils.handleError(message))
      }
      return (response.data ?? null) as T | null
    },
  })

  return {
    data: data ?? null,
    loading: isLoading,
    error: error ? api.utils.handleError(error) : null,
    refetch,
  }
}

// Hook for announcements
export function useAnnouncements(
  params: {
    category?: string
    featured?: boolean
    page?: number
    limit?: number
  } = {}
) {
  return useApiData<Announcement[]>(
    queryKeys.announcements.list({ ...params }),
    () => api.announcements.getAll(params)
  )
}

// Hook for single announcement
export function useAnnouncement(id: string) {
  return useApiData<Announcement>(queryKeys.announcements.detail(id), () =>
    api.announcements.getById(id)
  )
}

// Hook for news
export function useNews(
  params: {
    category?: string
    search?: string
    page?: number
    limit?: number
  } = {}
) {
  return useApiData<NewsItem[]>(queryKeys.news.list({ ...params }), () =>
    api.news.getAll(params)
  )
}

// Hook for single news item
export function useNewsItem(id: string) {
  return useApiData<NewsItem>(queryKeys.news.detail(id), () =>
    api.news.getById(id)
  )
}

// Hook for search functionality
export function useSearch() {
  const [results, setResults] = useState<SearchResult[]>([])
  const [error, setError] = useState<string | null>(null)

  const searchMutation = useMutation({
    mutationKey: queryKeys.search.all,
    mutationFn: async (params: SearchParams) => {
      const response = await api.search.search(params)
      if (!response || !api.utils.isSuccess(response)) {
        const message = response?.error || response?.message || 'Search failed'
        throw new Error(api.utils.handleError(message))
      }
      return response.data ?? []
    },
    onSuccess: data => {
      setResults(data as SearchResult[])
      setError(null)
    },
    onError: err => {
      setError(api.utils.handleError(err))
      setResults([])
    },
  })

  const advancedSearchMutation = useMutation({
    mutationKey: [...queryKeys.search.all, 'advanced'],
    mutationFn: async ({
      query,
      filters,
    }: {
      query: string
      filters: Record<string, any>
    }) => {
      const response = await api.search.advancedSearch(query, filters)
      if (!response || !api.utils.isSuccess(response)) {
        const message =
          response?.error || response?.message || 'Advanced search failed'
        throw new Error(api.utils.handleError(message))
      }
      return response.data ?? []
    },
    onSuccess: data => {
      setResults(data as SearchResult[])
      setError(null)
    },
    onError: err => {
      setError(api.utils.handleError(err))
      setResults([])
    },
  })

  const search = useCallback(
    async (params: SearchParams) => {
      setError(null)
      await searchMutation.mutateAsync(params)
    },
    [searchMutation]
  )

  const advancedSearch = useCallback(
    async (query: string, filters: Record<string, any>) => {
      setError(null)
      await advancedSearchMutation.mutateAsync({ query, filters })
    },
    [advancedSearchMutation]
  )

  const clearResults = useCallback(() => {
    setResults([])
    setError(null)
  }, [])

  return {
    results,
    loading: searchMutation.isPending || advancedSearchMutation.isPending,
    error,
    search,
    advancedSearch,
    clearResults,
  }
}

// Hook for managing pagination
export function usePagination(initialPage = 1, initialLimit = 10) {
  const [page, setPage] = useState(initialPage)
  const [limit, setLimit] = useState(initialLimit)

  const nextPage = useCallback(() => {
    setPage(prev => prev + 1)
  }, [])

  const prevPage = useCallback(() => {
    setPage(prev => Math.max(1, prev - 1))
  }, [])

  const goToPage = useCallback((newPage: number) => {
    setPage(Math.max(1, newPage))
  }, [])

  const changeLimit = useCallback((newLimit: number) => {
    setLimit(newLimit)
    setPage(1) // Reset to first page when changing limit
  }, [])

  const reset = useCallback(() => {
    setPage(initialPage)
    setLimit(initialLimit)
  }, [initialPage, initialLimit])

  return {
    page,
    limit,
    nextPage,
    prevPage,
    goToPage,
    changeLimit,
    reset,
  }
}

// Hook for managing filters
export function useFilters<T extends Record<string, any>>(initialFilters: T) {
  const [filters, setFilters] = useState<T>(initialFilters)

  const updateFilter = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }))
  }, [])

  const updateFilters = useCallback((newFilters: Partial<T>) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
    }))
  }, [])

  const clearFilter = useCallback(<K extends keyof T>(key: K) => {
    setFilters(prev => {
      const newFilters = { ...prev }
      delete newFilters[key]
      return newFilters
    })
  }, [])

  const clearAllFilters = useCallback(() => {
    setFilters(initialFilters)
  }, [initialFilters])

  const hasActiveFilters = useCallback(() => {
    return Object.keys(filters).some(
      key =>
        filters[key] !== undefined &&
        filters[key] !== null &&
        filters[key] !== ''
    )
  }, [filters])

  return {
    filters,
    updateFilter,
    updateFilters,
    clearFilter,
    clearAllFilters,
    hasActiveFilters,
  }
}

// Hook for managing loading states
export function useLoadingState() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const withLoading = useCallback(
    async <T>(asyncFunction: () => Promise<T>): Promise<T | null> => {
      try {
        setLoading(true)
        setError(null)
        const result = await asyncFunction()
        return result
      } catch (err: any) {
        setError(api.utils.handleError(err))
        return null
      } finally {
        setLoading(false)
      }
    },
    []
  )

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  return {
    loading,
    error,
    withLoading,
    clearError,
  }
}

// Hook for debounced values (useful for search)
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}
